import { styled } from '@linaria/react';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { formatKilometers, formatMeters } from '../../../core/mapV2/format';
import { perimeterQuery, selectionSummaryQuery } from '../../../core/mapV2/queries';
import theme from '../../../design/theme';
import { useAppSelector } from '../../../types';

const Bar = styled.footer`
    flex: 0 0 40px;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 16px;
    box-sizing: border-box;
    background-color: ${theme.color.white.main};
    border-top: 1px solid ${theme.color.grey.medium};
    font-size: 12px;
    color: ${theme.color.grey.dark};
    white-space: nowrap;
    overflow: hidden;
`;

const Strong = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12.5px;
    font-weight: 600;
`;

const Divider = styled.span`
    align-self: stretch;
    margin: 10px 0;
    border-left: 1px solid ${theme.color.grey.medium};
`;

// Reminds what is selected: label, length, category and gestionnaire of one section, or count, length and axes
// of several. With nothing selected it gives the size of the référentiel on the map (filtered over total when
// filters apply). Environment, traffic and last acquisition date from the maquette are not referential
// columns and are left out (see the open decisions in docs/map-v2/plan.md).
export const StatusBar: FC = () => {
    const { t } = useTranslation();
    const summary = useAppSelector(selectionSummaryQuery);
    const perimeter = useAppSelector(perimeterQuery);

    return (
        <Bar data-testid="map-v2-status-bar">
            {summary.kind === 'single' ? (
                <>
                    <Strong>
                        <PlaceOutlinedIcon fontSize="inherit" />
                        {summary.label}
                    </Strong>
                    <Divider />
                    <span>
                        {t('mapV2.statusBar.length')} : {formatMeters(summary.lengthMeters)}
                    </span>
                    {summary.category ? (
                        <>
                            <Divider />
                            <span>
                                {t('mapV2.statusBar.category')} : {summary.category}
                            </span>
                        </>
                    ) : null}
                    {summary.gestionary ? (
                        <>
                            <Divider />
                            <span>
                                {t('mapV2.statusBar.gestionary')} : {summary.gestionary}
                            </span>
                        </>
                    ) : null}
                </>
            ) : null}
            {summary.kind === 'multi' ? (
                <>
                    <Strong>
                        <PlaceOutlinedIcon fontSize="inherit" />
                        {t('mapV2.statusBar.selected', { count: summary.count })} ·{' '}
                        {formatKilometers(summary.lengthMeters)}
                    </Strong>
                    <Divider />
                    <span>
                        {t('mapV2.statusBar.axes')} : {summary.axes.join(', ')}
                    </span>
                </>
            ) : null}
            {summary.kind === 'none' && perimeter.total > 0 ? (
                <Strong data-testid="map-v2-status-perimeter">
                    {perimeter.restricted
                        ? t('mapV2.perimeter.filtered', { matching: perimeter.matching.length, total: perimeter.total })
                        : t('mapV2.perimeter.count', { count: perimeter.total })}{' '}
                    · {formatKilometers(perimeter.matchingLengthMeters)}
                </Strong>
            ) : null}
        </Bar>
    );
};
